import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  firstValueFrom,
  merge,
  Observable,
  Subject,
  throwError,
} from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
} from "rxjs/operators";
import { UserService } from "src/app/core/auth";
import { GlobalRequestPrefix } from "src/app/core/constants";
import { PaginationV2 } from "src/app/core/pagination-v2";
import { RequestV2Service } from "src/app/core/request-v2.service";
import { UserPreferencesService } from "src/app/core/user-preferences.service";
import {
  addOrUpdateBy,
  firstExistentValueFrom,
  getDefaultUpdater,
  isExistent,
  isInstanceOf,
  mapArrayUpdates,
  mapNullable,
  ODataResourceUrl,
  PartnerResource,
  pluckDistinctUntilChanged,
  SortOrder,
} from "src/utils";
import {
  Partner,
  PartnerAdd,
  PartnerListItem,
  PartnerListQueryModel,
  PartnerUpdate,
} from "./partner.model";

export const STORED_SELECTED_PARTNER_KEY = "SelectedPartner";
export const STORED_SELECTED_ROLE_KEY = "SelectedRole";

// TODO: Refactor this service to separate each user's type functionality in a separate class.
@Injectable({ providedIn: "root" })
export class PartnersService {
  public constructor(
    private readonly request: RequestV2Service,
    private readonly userPreferences: UserPreferencesService,
    private readonly user: UserService,
  ) {}

  private readonly updates = new Subject<PartnerListItem>();
  private readonly refreshNotifier = new Subject<void>();
  private readonly refreshFailures = new Subject<unknown>();
  private readonly refreshSuccesses = new Subject<void>();
  private hasLoadedPartnerSites = false;

  private readonly partnerChangingNotifier = new BehaviorSubject<boolean>(
    false,
  );

  public readonly partnerChangingChanges = this.partnerChangingNotifier.pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly selectedPartnerNotifier = new BehaviorSubject<void | null>(
    null,
  );

  public readonly changes = merge(this.updates, this.refreshNotifier).pipe(
    startWith(null),
    switchMap(() =>
      this.request
        .get({ prefix: GlobalRequestPrefix, path: "/partnersSites" })
        .pipe(
          filter(isExistent),
          map((value) => {
            const partners = Partner.deserializeList(value);
            this.hasLoadedPartnerSites = true;
            this.refreshSuccesses.next();
            return partners;
          }),
          // A failed reload keeps the last loaded partner sites available for
          // existing subscribers; only the initiating write sees the failure.
          // The very first read still fails loudly, as it always has.
          catchError((error: unknown) => {
            if (!this.hasLoadedPartnerSites) {
              return throwError(() => error);
            }
            this.refreshFailures.next(error);
            return EMPTY;
          }),
        ),
    ),
    shareReplay(1),
  );

  private readonly maybeSelectedForPartnerUserChanges = combineLatest([
    this.changes,
    this.userPreferences.partnerKeyChanges,
    this.userPreferences.siteIdChanges,
    this.selectedPartnerNotifier,
  ]).pipe(
    map(([partners, preferredPartnerKey, preferredSiteId]) => {
      if (!this.user.isPartner()) {
        throw new Error(
          'Partner user "selected partner" observable used for wrong user type.',
        );
      }

      if (partners.length === 0) {
        return "not-found";
      }

      // First, check for stored partner in the session storage
      const partnerStoredInSession = this.getStoredPartnerFromSession(partners);
      if (partnerStoredInSession) {
        return partnerStoredInSession;
      }

      // If no partner found in session storage, then search by preferredPartnerKey && preferredSiteId

      let preferredPartner: Partner | undefined;

      if (preferredPartnerKey && preferredSiteId) {
        preferredPartner = partners.find(
          (p) =>
            p.key.isSame(preferredPartnerKey) &&
            p.sites.map(({ id }) => id).includes(preferredSiteId),
        );
      }

      const preferredOrDefaultPartner = preferredPartner ?? partners[0];

      // Update the sessionStorage with the preferred or default partner
      this.updateStoredSelectedPartnerinSession(preferredOrDefaultPartner);
      return preferredOrDefaultPartner;
    }),
    shareReplay(1),
  );

  private readonly maybeSelectedForCarrierUserChanges = combineLatest([
    this.userPreferences.partnerKeyChanges,
    this.changes,
  ]).pipe(
    map(([key, partners]) => {
      if (!this.user.isCarrier()) {
        throw new Error("Wrong observable used for wrong user type 'Carrier'.");
      }

      const foundPartner =
        key && partners.find((partner) => partner.key.isSame(key));
      if (foundPartner) {
        return foundPartner;
      }

      const defaultPartner = partners.at(0);
      if (defaultPartner) {
        return defaultPartner;
      }

      throw new Error("No Partners Found in Partner List");
    }),
    shareReplay(1),
  );

  private readonly maybeSelectedForAdminUserChanges = combineLatest([
    this.userPreferences.partnerKeyChanges,
    this.changes,
  ]).pipe(
    map(([key, partners]) => {
      if (this.user.isPartner()) {
        throw new Error(
          'Global user "selected partner" observable used for wrong user type.',
        );
      }

      return (
        (key && partners.find((partner) => partner.key.isSame(key))) ||
        "not-found"
      );
    }),
    shareReplay(1),
  );

  private readonly maybeSelectedChanges = this.user.isLoadedChanges.pipe(
    take(1),
    switchMap(() => {
      if (this.user.isPartner()) {
        return this.maybeSelectedForPartnerUserChanges;
      } else if (this.user.isCarrier()) {
        return this.maybeSelectedForCarrierUserChanges;
      }

      return this.maybeSelectedForAdminUserChanges;
    }),
  );

  public readonly selectedChanges = this.maybeSelectedChanges.pipe(
    filter(isInstanceOf(Partner)),
    distinctUntilChanged(),
  );

  public readonly selectedRoleChanges = this.selectedChanges.pipe(
    pluckDistinctUntilChanged("role"),
    shareReplay(1),
  );

  public readonly isSelectedLoadedChanges = this.maybeSelectedChanges.pipe(
    map((partner) => partner !== "not-found"),
  );

  private readonly defaultSelectionChanges = this.changes.pipe(
    map((partners) => (partners[0] ?? null) as Partner | null),
  );

  private isSelectedLoadedForGlobalUserNotifier?: Observable<boolean>;

  private readonly unloadNotifier = new Subject<void>();

  private getStoredPartnerFromSession(
    partners: readonly Partner[],
  ): Partner | null {
    const storedSelectedPartnerKey = sessionStorage.getItem(
      STORED_SELECTED_PARTNER_KEY,
    );
    const storedSelectedRole = sessionStorage.getItem(STORED_SELECTED_ROLE_KEY);

    if (storedSelectedPartnerKey && storedSelectedRole) {
      const matchedPartner = partners.find(
        (p) =>
          p.key.toString() === storedSelectedPartnerKey &&
          p.role.toString() === storedSelectedRole,
      );
      if (matchedPartner) {
        return matchedPartner;
      }
    }

    return null;
  }

  private updateStoredSelectedPartnerinSession(partner: Partner): void {
    sessionStorage.setItem(
      STORED_SELECTED_PARTNER_KEY,
      `${partner.key.toString()}`,
    );

    sessionStorage.setItem(STORED_SELECTED_ROLE_KEY, partner.role.toString());
  }

  public getListChanges({
    pagination = new PaginationV2(),
  }: {
    pagination?: PaginationV2;
  } = {}): Observable<readonly PartnerListItem[]> {
    return this.request
      .getPaged({
        prefix: GlobalRequestPrefix,
        path: "/partners/all",
        data: new PartnerListQueryModel({
          pagination,
          sort: { key: "name", order: SortOrder.Ascending },
        }),
        pagination,
      })
      .pipe(
        mapNullable((response) =>
          PartnerListItem.deserializeList(response.items),
        ),
        mapArrayUpdates(this.updates, (all, update) =>
          addOrUpdateBy(all, update, (partner) =>
            partner.key.isSame(update.key),
          ),
        ),
        filter(isExistent),
        shareReplay(1),
      );
  }

  public updatePartnerChangingStatus(isChanging: boolean): void {
    this.partnerChangingNotifier.next(isChanging);
  }

  /**
   * Reloads the shared partner and site list after a write that can change it,
   * and resolves once that reload has been confirmed, or rejects if it failed.
   */
  public refreshPartners(): Promise<void> {
    const settled = firstValueFrom(
      merge(
        // Keeps the shared pipeline hot so the notifier below is observed,
        // without letting the replayed pre-refresh list settle this promise.
        this.changes.pipe(ignoreElements()),
        this.refreshSuccesses,
        this.refreshFailures.pipe(
          switchMap((error: unknown) => throwError(() => error)),
        ),
      ),
    );
    this.refreshNotifier.next();
    return settled;
  }

  public async load(): Promise<boolean> {
    if (this.user.isPartner()) {
      const partner = await firstValueFrom(
        this.maybeSelectedForPartnerUserChanges,
      );

      if (partner !== "not-found") {
        await this.setSelected(partner);
        return true;
      }

      return false;
    }

    if (!this.isSelectedLoadedForGlobalUserNotifier) {
      // Load the cache first so the default selection doesn't get applied on
      // initial load, and only runs on subsequent changes.
      await firstExistentValueFrom(this.changes);

      const [updater, notifier] = getDefaultUpdater(
        this.maybeSelectedForAdminUserChanges,
        this.defaultSelectionChanges,
      );
      this.isSelectedLoadedForGlobalUserNotifier = notifier;

      // Set the default partner (for global/admin users only)
      // if one isn't selected or the selected wasn't found.
      updater
        .pipe(
          switchMap((partner) => this.setSelected(partner)),
          takeUntil(this.unloadNotifier),
        )
        .subscribe();
    }

    return firstValueFrom(this.isSelectedLoadedForGlobalUserNotifier);
  }

  public async create(update: PartnerAdd): Promise<PartnerListItem> {
    const response = await firstExistentValueFrom(
      this.request.post({
        prefix: GlobalRequestPrefix,
        path: "/partners",
        data: update,
      }),
    );
    const partner = PartnerListItem.deserialize(response);
    this.updates.next(partner);
    return partner;
  }

  public async update(
    update: PartnerUpdate,
    partnerKey: string,
  ): Promise<PartnerListItem> {
    const response = await firstValueFrom(
      this.request.patch({
        prefix: GlobalRequestPrefix,
        path: "/partners/{partnerKey}",
        data: update,
        pathParams: { partnerKey },
      }),
    );
    const partner = PartnerListItem.deserialize(response);
    this.updates.next(partner);
    return partner;
  }

  /**
   * Gets the concrete `ODataResourceUrl` for a `PartnerResource` that is nested
   * within the appropriate, currently selected partner.
   *
   * **Example result URL:**
   *
   * `https://dev-mr.capstonelogistics.com/api/partner/1234/docks`
   *
   * @param resource The partner resource to add the partner to.
   */
  public getResourceInSelected<Resource, ResourceName>(
    resource: PartnerResource<Resource, ResourceName>,
  ): Observable<ODataResourceUrl<Resource, ResourceName>> {
    return this.selectedChanges.pipe(map((partner) => resource.get(partner)));
  }

  public async setSelected(partner: Partner): Promise<void> {
    if (this.user.isPartner()) {
      // Update sessionStorage with partner key
      sessionStorage.setItem(
        STORED_SELECTED_PARTNER_KEY,
        `${partner.key.toString()}`,
      );

      this.selectedPartnerNotifier.next();
      this.user.updatePermissions(partner.role);
    }

    await this.userPreferences.setPartnerKey(partner.key, {
      sessionOnly: this.user.isCarrier(),
    });
  }
}
